import React, { useEffect, useState } from "react";
import { useClientSideSplitTreatments } from "@leafly-com/split-next";
import { useRouter } from "next/router";
import pluralize from "pluralize";
import { useDispatch, useSelector, useStore } from "react-redux";

import { getDispensary } from "api/requests/getDispensary";
import { retrieveCart } from "api/requests/retrieveCart";
import { useChildrenBlurEvent } from "hooks/useChildrenBlurEvent";
import { useEventTracker } from "hooks/useEventTracker";
import {
  AddedToCartAction,
  DismissAddToBagPopover,
  LoadCartAction,
  RESERVATION_ADDED_TO_BAG,
  RESERVATION_DISMISS_ADDED_TO_BAG,
  RESERVATION_LOAD_CART,
} from "redux/reducers/reservation";
import { getDomainCountryCode } from "redux/selectors/config";
import {
  getCartId,
  getCartItemCount,
  getLastItemAddedToBag,
  getShowingAddedToBagPopover,
} from "redux/selectors/reservation";
import { isInValidationGroup } from "utils/dispensaryUtils";
import { getSplitKey } from "utils/split/getSplitKey";
import { Action, Category, Label } from "utils/trackEvent";

import { useIsJaneCheckoutEnabled } from "components/Checkout/JaneCheckout";
import CartIcon from "components/Icons/cart.svg";
import CloseIcon from "components/Icons/x.svg";
import Image from "components/Image";

/* eslint-disable sort-keys-fix/sort-keys-fix -- exception: order is important */
const GRAM_WEIGHT_CONVERSIONS = {
  "1.0": "1g",
  "3.5": "1/8 oz",
  "7.0": "1/4 oz",
  "14.0": "1/2 oz",
  "28.0": "1 oz",
};
/* eslint-enable sort-keys-fix/sort-keys-fix -- exception: order is important */

// @ts-ignore (fix me please, do not replicate)
const normalizeGramsToOunces = ({ packageUnit, packageSize }) => {
  return (
    // @ts-ignore (fix me please, do not replicate)
    (packageUnit === "g" && GRAM_WEIGHT_CONVERSIONS[packageSize]) ||
    `${packageSize} ${packageUnit}`
  );
};

const ArrowNotch = () => (
  <div
    className="absolute bg-white border-l border-leafly-white border-t top-0"
    style={{
      height: "16px",
      right: "4px",
      transform: "translate(-50%, -50%) rotate(45deg)",
      width: "16px",
    }}
  ></div>
);

const CartCountIndicator = () => {
  const cartItemCount = useSelector(getCartItemCount);
  return (
    <div
      className="absolute flex items-center justify-center leading-none opacity-100 pointer-events-none rounded-full text-white text-xs bg-green"
      style={{
        minHeight: "20px",
        minWidth: "20px",
        opacity: cartItemCount ? undefined : "0",
        right: "-12px",
        top: "-8px",
        transition: "opacity 250ms linear",
      }}
    >
      {cartItemCount}
    </div>
  );
};

const ViewCartButton = () => {
  const { publishEvent } = useEventTracker();
  return (
    <a
      className="button block text-sm"
      href="/bag"
      onClick={() => {
        publishEvent({
          action: Action.click,
          category: Category.addToBag,
          label: Label.viewBag,
        });
      }}
      data-testid="header-cart-view-cart-button"
    >
      view cart
    </a>
  );
};

// @ts-ignore (fix me please, do not replicate)
const CartItemImage = ({ cartItem }) =>
  cartItem?.menuItemData?.imageUrl && (
    <div className="mr-sm" style={{ width: 56 }}>
      <Image
        src={cartItem.menuItemData.imageUrl}
        alt={cartItem.menuItemData.name}
        sizes={[56]}
      />
    </div>
  );

const AddToBagPopOver = React.forwardRef<
  HTMLDivElement,
  {
    showAddToBagPopover: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
    lastItemAddedInBag: any;
  }
>(({ showAddToBagPopover, lastItemAddedInBag }, ref) => {
  const [dispensaryId, setDispensaryId] = useState<number>();
  const {
    query: { slug },
  } = useRouter();
  const { publishEvent } = useEventTracker();
  const dispatch = useDispatch();
  const isJaneCheckoutEnabled = useIsJaneCheckoutEnabled();

  const { treatments, isLoading } = useClientSideSplitTreatments(
    "shopEveryMenuMVP_test",
    getSplitKey()
  );
  const shopEveryMenuMVPEnabled =
    !isLoading && treatments.shopEveryMenuMVP_test.treatment === "on";

  const isInShopEveryMenuTest =
    isInValidationGroup(dispensaryId) && shopEveryMenuMVPEnabled;

  useEffect(() => {
    const getDispensaryId = async () => {
      const dispensary = await getDispensary(slug as string);
      setDispensaryId(dispensary?.id);
    };
    getDispensaryId();
  }, [slug]);

  React.useEffect(() => {
    if (showAddToBagPopover) {
      publishEvent({
        action: Action.impression,
        category: Category.addToBag,
        janeCheckoutEnabled: isJaneCheckoutEnabled,
        label: isInShopEveryMenuTest
          ? "confirmation - shop every menu"
          : Label.confirmation,
        menuItemId: lastItemAddedInBag.id,
      });
    }
  }, [showAddToBagPopover]);

  return (
    <React.Fragment>
      {showAddToBagPopover && (
        <div
          ref={ref}
          className="absolute bg-white right-0 text-default rounded elevation-low"
          style={{
            pointerEvents: "all",
            right: "-8px",
            top: "36px",
            transition: "all 0.25s ease-in-out",
            width: "300px",
          }}
        >
          <ArrowNotch />
          <div className="flex justify-between items-center">
            <span className="text-xs font-extrabold flex-shrink-0 pl-lg uppercase">
              {isInShopEveryMenuTest
                ? "item added to in-store cart"
                : "item added to cart"}
            </span>
            <button
              id="added-to-bag-close"
              className="p-lg"
              aria-label="Close item added to cart popup"
              onClick={() => {
                dispatch({
                  type: RESERVATION_DISMISS_ADDED_TO_BAG,
                });
              }}
            >
              <CloseIcon height="22" width="22" />
            </button>
          </div>
          <div className="flex px-lg">
            <CartItemImage cartItem={lastItemAddedInBag} />

            <div
              data-testid="cart-item"
              role="status"
              aria-label="Added to cart"
            >
              <div className="mb-xs">
                {lastItemAddedInBag.menuItemData?.name}
              </div>
              <span className="text-grey text-xs block">
                {normalizeGramsToOunces(lastItemAddedInBag)}
              </span>
            </div>
          </div>
          <div className="p-lg text-center">
            <ViewCartButton />
          </div>
        </div>
      )}
    </React.Fragment>
  );
});

export const Cart = () => {
  const cartId = useSelector(getCartId);
  const dispatch = useDispatch();
  const store = useStore();
  const showAddToBagPopover = useSelector(getShowingAddedToBagPopover);
  const lastItemAddedInBag = useSelector(getLastItemAddedToBag);
  const { publishEvent } = useEventTracker();
  const cartItemCount = useSelector(getCartItemCount);
  const domainCountryCode = useSelector(getDomainCountryCode);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  const getCartListener = async (e?: any) => {
    if (!cartId) {
      return;
    }
    const { cart } = await retrieveCart({
      cartId: cartId,
      domainCountryCode: domainCountryCode,
    });
    const variantId = Number(e?.detail?.variantId);

    if (!cart) {
      return;
    }

    dispatch<LoadCartAction>({
      cart,
      type: RESERVATION_LOAD_CART,
    });

    if (variantId) {
      dispatch<AddedToCartAction>({
        type: RESERVATION_ADDED_TO_BAG,
        variantId,
      });
    }
  };

  useEffect(() => {
    document.addEventListener("getcart", getCartListener);
    getCartListener();

    return () => {
      document.removeEventListener("getcart", getCartListener);
    };
  }, [cartId]);

  const { ref } = useChildrenBlurEvent<HTMLDivElement>(() => {
    // Note: You can't use a useSelector hook here - the value won't update. This calls the selector manually.
    if (getShowingAddedToBagPopover(store.getState())) {
      dispatch<DismissAddToBagPopover>({
        type: RESERVATION_DISMISS_ADDED_TO_BAG,
      });
    }
  });

  return (
    <>
      <nav className="flex-shrink-0 relative ml-lg" aria-label="Cart">
        <a
          data-testid="cart"
          href="/bag"
          className="flex items-center relative"
          aria-label={`Shopping bag. ${pluralize(
            "item",
            cartItemCount ?? 0,
            true
          )} in cart`}
          onClick={() =>
            publishEvent({
              action: Action.click,
              category: Category.header,
              label: "cart",
            })
          }
        >
          <CartIcon height="24" width="24" />
          <CartCountIndicator />
        </a>
        <AddToBagPopOver
          ref={ref}
          showAddToBagPopover={showAddToBagPopover}
          lastItemAddedInBag={lastItemAddedInBag}
        />
      </nav>
    </>
  );
};
